import { useSelector } from "react-redux";
import {
  collection,
  doc,
  setDoc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { firestore } from "services/firebaseService/firebaseConfig";
import useNotificationActions from "modules/notification/hooks/useNotificationActions";
import { useI18n } from "i18n";
import logger from "logging/logger";
import { ShuffleStates } from "../../constants";
import {
  makeSelectShowInitShuffleBanner,
  makeSelectShowShuffleCountdownBanner,
  makeSelectShowShuffleCountdownDialog,
  makeSelectShowShuffleDialog,
} from "../../redux/selectors";

// TODO: Optional properties not needed in ShuffleV2
export interface IShuffleAttendeesDoc {
  timestamp: string;
  theater?: string;
  initiatedBy: string;
  status: ShuffleStates;
  companyId?: string;
  plan?: string;
  targetGroupSize: number | null;
}

export const useShuffleAttendees = () => {
  const showShuffleAttendeesDialog = useSelector(makeSelectShowShuffleDialog());
  const { addErrorNotification } = useNotificationActions();
  const showShuffleAttendeesCountdown = useSelector(
    makeSelectShowShuffleCountdownDialog(),
  );
  const showShuffleAttendeesBanner = useSelector(
    makeSelectShowShuffleCountdownBanner(),
  );
  const { t } = useI18n(["event"]);

  const showInitShuffleBanner = useSelector(makeSelectShowInitShuffleBanner());

  const firestoreDoc = "ShuffleAttendeesV2";

  const getShuffleAttendeesDoc = (eventId: string) =>
    doc(collection(firestore, firestoreDoc), eventId);

  const addShuffleAttendeesDoc = async (
    eventId: string,
    shuffleAttendeesDoc: Omit<IShuffleAttendeesDoc, "status" | "timestamp">,
  ) => {
    logger.info(
      `[addShuffleAttendeesDoc] event: ${eventId} theater: ${shuffleAttendeesDoc.theater} initiatedBy: ${shuffleAttendeesDoc.initiatedBy} targetGroupSize:${shuffleAttendeesDoc.targetGroupSize}`,
    );
    try {
      await setDoc(getShuffleAttendeesDoc(eventId), {
        ...shuffleAttendeesDoc,
        timestamp: serverTimestamp(),
        status: ShuffleStates.COUNTDOWN,
      });
    } catch (err) {
      addErrorNotification({
        autoDismiss: 5,
        title: t("event:shuffle.attendees"),
        message: t("event:shuffle.attendees.error"),
      });
      throw err;
    }
  };

  const cancelShuffleAttendeesDoc = (eventId: string) => {
    logger.info(`[cancelShuffleAttendeesDoc] event: ${eventId}`);
    updateDoc(getShuffleAttendeesDoc(eventId), {
      timestamp: serverTimestamp(),
      status: ShuffleStates.CANCELLED,
    });
  };

  return {
    showShuffleAttendeesDialog,
    showShuffleAttendeesCountdown,
    showShuffleAttendeesBanner,
    showInitShuffleBanner,
    getShuffleAttendeesDoc,
    addShuffleAttendeesDoc,
    cancelShuffleAttendeesDoc,
  };
};
